'use client'

import Image from 'next/image'
import { Heart, Leaf, Shield } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { aboutTranslations } from '@/translations/about'
import { motion } from 'framer-motion'

export default function About() {
  const { language } = useLanguage()
  const t = aboutTranslations[language]

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-white to-teal-50/15">
      <div className="container mx-auto px-4">
        {/* Mobile: Title, subtitle, then image below */}
        <div className="md:hidden mb-6 text-center">
          <Image
            src="/images/about/selfie-mobile.webp"
            alt={language === 'en' ? 'James Carling - Professional Gardener' : 'James Carling - Garddwr Proffesiynol'}
            className="mx-auto max-w-[38%] h-auto mb-4"
            width={200}
            height={200}
            loading="lazy"
          />
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            {language === 'en'
              ? <>
                  Hi, I&apos;m James Carling, your local gardener
                </>
              : <>
                  {t.title.split('James Carling')[0]}
                  James Carling
                  {t.title.split('James Carling')[1]}
                </>
            }
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            {language === 'en' && t.subtitle.includes('James Carling') ? (
              <>
                {t.subtitle.split('James Carling')[0]}
                James Carling
                {t.subtitle.split('James Carling')[1]}
              </>
            ) : t.subtitle}
          </p>
          <div className="mt-6">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl p-8 card-sharp">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-sharp">{t.certifications.title} & Insurance</h3>
              <div className="text-gray-700 space-y-2 text-base md:text-lg text-center">
                {t.certifications.list.map((item, idx) => (
                  <div key={idx} className="py-1">{item}</div>
                ))}
                <div className="py-1">{language === 'en' ? 'Fully insured with public liability insurance' : 'Wedi\'i yswirio\'n llawn gyda yswiriant atebolrwydd cyhoeddus'}</div>
              </div>
            </div>
            <div className="space-y-8 mt-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <Leaf className="w-6 h-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 text-sharp">
                  {language === 'en' ? 'Eco-Friendly Approach' : 'Agwedd Eco-gyfeillgar'}
                </h3>
                <p className="text-gray-700 leading-relaxed text-sharp">
                  {language === 'en' 
                    ? 'Committed to sustainable gardening practices that protect our environment while creating beautiful outdoor spaces.'
                    : 'Ymroddedig i arferion arddio cynaliadwy sy\'n amddiffyn ein hamgylchedd wrth greu lleoedd awyr agored hardd.'
                  }
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <Heart className="w-6 h-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 text-sharp">
                  {language === 'en' ? 'Passionate Care' : 'Gofal Angerddol'}
                </h3>
                <p className="text-gray-700 leading-relaxed text-sharp">
                  {language === 'en' 
                    ? 'Every garden is treated with the same care and attention I would give to my own, ensuring the best results for your outdoor space.'
                    : 'Mae pob gardd yn cael ei thrin gyda\'r un gofal a sylw y byddwn i\'n ei roi i\'m gardd fy hun, gan sicrhau y canlyniadau gorau ar gyfer eich lle awyr agored.'
                  }
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <Shield className="w-6 h-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 text-sharp">
                  {language === 'en' ? 'Professional Standards' : 'Safonau Proffesiynol'}
                </h3>
                <p className="text-gray-700 leading-relaxed text-sharp">
                  {language === 'en' 
                    ? 'Certified and experienced, I bring professional expertise to every project while maintaining the highest standards of quality and safety.'
                    : 'Ardystiedig a phrofiadol, rwy\'n dod ag arbenigedd proffesiynol i bob prosiect wrth gynnal y safonau uchaf o ansawdd a diogelwch.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Desktop: Keep existing grid layout */}
        <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative flex flex-col items-center justify-center order-1 lg:order-none mb-12 lg:mb-0" style={{ minHeight: '20rem' }}>
            <Image
              src="/images/about/selfie.webp"
              alt={language === 'en' ? 'James Carling - Professional Gardener' : 'James Carling - Garddwr Proffesiynol'}
              className="max-w-[58%] h-auto"
              width={300}
              height={300}
              loading="lazy"
            />
            <div className="mt-6 w-full">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl p-8 card-sharp">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-sharp">{t.certifications.title} & Insurance</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 text-lg">
                  {t.certifications.list.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                  <li>{language === 'en' ? 'Fully insured with public liability insurance' : 'Wedi\'i yswirio\'n llawn gyda yswiriant atebolrwydd cyhoeddus'}</li>
                </ul>
              </div>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8 order-2 lg:order-none"
          >
            <h2 className="text-4xl font-bold text-gray-900 text-sharp" style={{ fontFamily: 'Playfair Display, serif' }}>
              {language === 'en'
                ? <>
                    Hi, I&apos;m James Carling, your local gardener
                  </>
                : <>
                    {t.title.split('James Carling')[0]}
                    James Carling
                    {t.title.split('James Carling')[1]}
                  </>
              }
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed text-sharp">
              {language === 'en' && t.subtitle.includes('James Carling') ? (
                <>
                  {t.subtitle.split('James Carling')[0]}
                  James Carling
                  {t.subtitle.split('James Carling')[1]}
                </>
              ) : t.subtitle}
            </p>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Leaf className="w-6 h-6 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 text-sharp">
                    {language === 'en' ? 'Eco-Friendly Approach' : 'Agwedd Eco-gyfeillgar'}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-sharp">
                    {language === 'en' 
                      ? 'Committed to sustainable gardening practices that protect our environment while creating beautiful outdoor spaces.'
                      : 'Ymroddedig i arferion arddio cynaliadwy sy\'n amddiffyn ein hamgylchedd wrth greu lleoedd awyr agored hardd.'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 text-sharp">
                    {language === 'en' ? 'Passionate Care' : 'Gofal Angerddol'}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-sharp">
                    {language === 'en' 
                      ? 'Every garden is treated with the same care and attention I would give to my own, ensuring the best results for your outdoor space.'
                      : 'Mae pob gardd yn cael ei thrin gyda\'r un gofal a sylw y byddwn i\'n ei roi i\'m gardd fy hun, gan sicrhau y canlyniadau gorau ar gyfer eich lle awyr agored.'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 text-sharp">
                    {language === 'en' ? 'Professional Standards' : 'Safonau Proffesiynol'}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-sharp">
                    {language === 'en' 
                      ? 'Certified and experienced, I bring professional expertise to every project while maintaining the highest standards of quality and safety.'
                      : 'Ardystiedig a phrofiadol, rwy\'n dod ag arbenigedd proffesiynol i bob prosiect wrth gynnal y safonau uchaf o ansawdd a diogelwch.'
                    }
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg"
              >
                {language === 'en' ? 'Get Free Quote' : 'Cael Dyfynbris Am Ddim'}
              </a>
              <a
                href="#portfolio"
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-300 border border-gray-200"
              >
                {language === 'en' ? 'View Portfolio' : 'Gweld Portfolio'}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

/* 
Image URLs to download from Unsplash:

1. About Image (garden-about.jpg):
https://unsplash.com/photos/person-holding-green-plant-8eH5yJ0PzUY

Note: All images from Unsplash are free to use for commercial and noncommercial purposes.
No attribution is required, but it's appreciated.
*/ 